import { Link } from "react-router-dom";
import { avatarSrc } from "../../lib/api";
import { seasonStart } from "../../hooks/useCWeek";
import { Avatar, shortDate } from "./kit";

const seasonLabel = () => {
  const y = Number(seasonStart().slice(0, 4));
  return `${y}\u2013${String(y + 1).slice(2)}`;
};

const byPlayer = (awards) => {
  const map = new Map();
  awards.forEach((a) => {
    const row = map.get(a.player_id) ?? { player_id: a.player_id, name: a.player_name, total: 0, cs: {}, weeks: [] };
    row.total += 1;
    row.cs[a.c_name] = (row.cs[a.c_name] ?? 0) + 1;
    row.weeks.push(a.week);
    map.set(a.player_id, row);
  });
  return [...map.values()].sort((x, y) => y.total - x.total || (y.weeks[0] > x.weeks[0] ? 1 : -1));
};

const CTiles = ({ items, counts }) => {
  const top = Math.max(0, ...Object.values(counts));
  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
      {items.map((it) => {
        const n = counts[it.name] ?? 0;
        return (
          <li key={it.name} data-testid={`award-wall-c-${it.name}`} className={`rounded-xl border p-3 ${n && n === top ? "border-[#39FF14] bg-[#39FF14]/10" : "border-[#1E293B] bg-[#0F172A]"}`}>
            <p className="truncate font-mono-gd text-[11px] uppercase tracking-wider text-slate-300">{it.name}</p>
            <p data-testid={`award-wall-c-count-${it.name}`} className={`font-display text-3xl font-black leading-none ${n ? "text-white" : "text-slate-600"}`}>{n}</p>
          </li>
        );
      })}
    </ul>
  );
};

const WinnerRow = ({ tid, r }) => (
  <li data-testid={`award-wall-player-${r.player_id}`} className="flex items-center gap-3 rounded-xl border border-[#1E293B] bg-[#0F172A] p-3">
    <Avatar name={r.name} src={avatarSrc(tid, { id: r.player_id, has_app_photo: true })} size="h-10 w-10" />
    <div className="min-w-0 flex-1">
      <Link to={`/coach/players/${r.player_id}`} className="block truncate font-semibold text-white hover:text-[#39FF14]">{r.name}</Link>
      <div className="mt-1 flex flex-wrap gap-1">
        {Object.entries(r.cs).map(([c, n]) => (
          <span key={c} className="rounded-full bg-[#162032] px-2 py-0.5 font-mono-gd text-[10px] uppercase tracking-wider text-slate-200">{c}{n > 1 ? ` \u00d7${n}` : ""}</span>
        ))}
      </div>
      <p className="mt-1 truncate font-mono-gd text-[10px] uppercase tracking-wider text-slate-400">Weeks of {r.weeks.map((w) => shortDate(w)).join(", ")}</p>
    </div>
    <span data-testid={`award-wall-total-${r.player_id}`} className="font-display text-3xl font-black text-[#39FF14]">{r.total}×</span>
  </li>
);

/** Season wall of every 7 C's Player of the Week for a level, with how often each C was awarded. */
export const AwardWall = ({ tid, level, items, season }) => {
  const awards = season.filter((a) => a.level === level);
  const counts = awards.reduce((m, a) => ({ ...m, [a.c_name]: (m[a.c_name] ?? 0) + 1 }), {});
  const rows = byPlayer(awards);
  return (
    <div data-testid="award-wall" className="mt-6 border-t border-[#1E293B] pt-5">
      <div className="mb-4 flex flex-wrap items-baseline gap-3">
        <h3 className="font-display text-2xl font-bold uppercase text-white">Season award wall</h3>
        <p className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">{seasonLabel()} · <span data-testid="award-wall-total">{awards.length}</span> award{awards.length === 1 ? "" : "s"}</p>
      </div>
      <CTiles items={items} counts={counts} />
      {rows.length ? (
        <ul className="mt-4 grid gap-2 md:grid-cols-2">{rows.map((r) => <WinnerRow key={r.player_id} tid={tid} r={r} />)}</ul>
      ) : <p data-testid="award-wall-empty" className="mt-4 text-sm text-slate-300">No awards yet this season.</p>}
    </div>
  );
};
